import { useState } from 'react';

import axios, { AxiosResponse } from 'axios';

import { useAxiosRequest } from '@admin/hooks';

type NamedResource = {
  id: number | string;
  name: string;
  region_ids?: number[];
  active?: boolean;
};

type DispatcherDepotResource = NamedResource & {
  address?: {
    latitude?: number | string;
    longitude?: number | string;
    line1?: string;
  };
};

type VehicleType = {
  id: number | string;
  name: string;
  num_seats?: number;
  cuft?: number;
};

type DispatcherUser = {
  id: number | string;
  roles: string[];
};

export type DispatcherResources = {
  depots: DispatcherDepotResource[];
  warehouses: NamedResource[];
  vehicleTypes: VehicleType[];
  users: DispatcherUser[];
};

export const useDispatcherResources = () => {
  const [depotsRequest] = useState(() => axios.get<NamedResource[]>('/depots.json', { params: { status: 'all' } }));
  const [warehousesRequest] = useState(() => axios.get<NamedResource[]>('/warehouses.json'));
  const [vehicleTypesRequest] = useState(() => axios.get<VehicleType[]>('/vehicle_types.json'));
  const [usersRequest] = useState(() => axios.get<DispatcherUser[]>('/dispatching/users.json'));

  const depotsResult = useAxiosRequest(depotsRequest);
  const warehousesResult = useAxiosRequest(warehousesRequest);
  const vehicleTypesResult = useAxiosRequest(vehicleTypesRequest);
  const usersResult = useAxiosRequest(usersRequest);

  const loading = depotsResult.loading || warehousesResult.loading || vehicleTypesResult.loading || usersResult.loading;
  const error = depotsResult.error ?? warehousesResult.error ?? vehicleTypesResult.error ?? usersResult.error;

  const resources: DispatcherResources | undefined =
    depotsResult.response && warehousesResult.response && vehicleTypesResult.response && usersResult.response
      ? {
          depots: depotsResult.response.data,
          warehouses: warehousesResult.response.data,
          vehicleTypes: vehicleTypesResult.response.data,
          users: usersResult.response.data,
        }
      : undefined;

  return { resources, loading, error };
};
